import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Check, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

import { useLang } from "@/context/LanguageContext";
import { LESSONS, SLOTS, LANGS } from "@/i18n/content";
import { Reveal } from "@/components/site/Reveal";
import { createBooking } from "@/lib/api";

const MAX_DAYS = 60;

export default function Booking({ preset }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    lesson_type: "",
    time_slot: "",
    people: "1",
    language: lang,
  });
  const [date, setDate] = useState(null);
  const [notes, setNotes] = useState("");
  const [dateOpen, setDateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (preset) setForm((f) => ({ ...f, lesson_type: preset }));
  }, [preset]);

  useEffect(() => {
    setForm((f) => ({ ...f, language: lang }));
  }, [lang]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !emailValid(form.email) ||
      !form.phone.trim() ||
      !form.lesson_type ||
      !form.time_slot ||
      !date
    ) {
      toast.error(t.booking.required);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        people: parseInt(form.people, 10),
        date: format(date, "yyyy-MM-dd"),
        notes,
      };
      await createBooking(payload);
      setSuccess({
        name: form.name,
        lesson: t.lessons.names[form.lesson_type],
        date: format(date, "PPP"),
        slot: t.schedule.slotNames[form.time_slot],
      });
    } catch (err) {
      toast.error(t.booking.error);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSuccess(null);
    setForm({ name: "", email: "", phone: "", lesson_type: "", time_slot: "", people: "1", language: lang });
    setDate(null);
    setNotes("");
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + MAX_DAYS);

  const inputCls =
    "rounded-none border-black/15 bg-transparent h-12 focus-visible:ring-1 focus-visible:ring-navy font-manrope";

  const fill = (str, obj) => str.replace(/\{(\w+)\}/g, (_, k) => obj[k] ?? "");

  return (
    <section id="booking" data-testid="booking-section" className="bg-cream py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left column: heading */}
          <div className="lg:col-span-4">
            <Reveal>
              <p className="label-mono text-ocean mb-6">{t.booking.label}</p>
              <h2 className="font-outfit font-semibold text-4xl md:text-6xl tracking-tight text-ink leading-[0.95]">
                {t.booking.title}
              </h2>
              <p className="font-manrope text-ink/60 text-base md:text-lg mt-6">{t.booking.sub}</p>
              <div className="mt-10 border-t border-black/10 pt-8 space-y-4">
                {LESSONS.map((l) => (
                  <div key={l.id} className="flex items-baseline justify-between">
                    <span className="font-manrope text-ink/70">{t.lessons.names[l.id]}</span>
                    <span className="font-mono text-navy">€{l.price}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right column: form / success */}
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal>
              <div className="border border-black/10 bg-surface p-6 md:p-10">
                <AnimatePresence mode="wait">
                  {success ? (
                    <motion.div
                      key="success"
                      data-testid="booking-success"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center py-10"
                    >
                      <div className="w-16 h-16 rounded-full bg-ocean text-white flex items-center justify-center mx-auto mb-6">
                        <Check size={30} />
                      </div>
                      <h3 className="font-playfair text-3xl md:text-4xl text-ink mb-4">
                        {t.booking.successTitle}
                      </h3>
                      <p className="font-manrope text-ink/70 max-w-md mx-auto">
                        {fill(t.booking.successMsg, success)}
                      </p>
                      <button
                        data-testid="booking-reset-btn"
                        onClick={reset}
                        className="mt-8 border border-navy text-navy label-mono px-6 py-3 hover:bg-navy hover:text-white transition-colors"
                      >
                        {t.booking.another}
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      onSubmit={submit}
                      data-testid="booking-form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="grid grid-cols-1 md:grid-cols-2 gap-5"
                    >
                      <div className="md:col-span-2">
                        <Label className="label-mono text-ink/70">{t.booking.name}</Label>
                        <Input
                          data-testid="booking-name"
                          className={inputCls + " mt-2"}
                          value={form.name}
                          onChange={(e) => set("name", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="label-mono text-ink/70">{t.booking.email}</Label>
                        <Input
                          data-testid="booking-email"
                          type="email"
                          className={inputCls + " mt-2"}
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="label-mono text-ink/70">{t.booking.phone}</Label>
                        <Input
                          data-testid="booking-phone"
                          className={inputCls + " mt-2"}
                          value={form.phone}
                          onChange={(e) => set("phone", e.target.value)}
                        />
                      </div>

                      <div>
                        <Label className="label-mono text-ink/70">{t.booking.lesson}</Label>
                        <Select value={form.lesson_type} onValueChange={(v) => set("lesson_type", v)}>
                          <SelectTrigger data-testid="booking-lesson" className={inputCls + " mt-2"}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            {LESSONS.map((l) => (
                              <SelectItem key={l.id} value={l.id} data-testid={`booking-lesson-${l.id}`}>
                                {t.lessons.names[l.id]} · €{l.price}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="label-mono text-ink/70">{t.booking.date}</Label>
                        <Popover open={dateOpen} onOpenChange={setDateOpen}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              data-testid="booking-date-trigger"
                              className={`${inputCls} mt-2 w-full flex items-center justify-between px-3 text-left ${
                                date ? "text-ink" : "text-ink/40"
                              }`}
                            >
                              {date ? format(date, "PPP") : t.booking.pickDate}
                              <CalendarIcon size={16} className="opacity-50" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 rounded-none" align="start">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={(d) => {
                                setDate(d);
                                setDateOpen(false);
                              }}
                              disabled={{ before: today, after: maxDate }}
                              defaultMonth={date || today}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div>
                        <Label className="label-mono text-ink/70">{t.booking.slot}</Label>
                        <Select value={form.time_slot} onValueChange={(v) => set("time_slot", v)}>
                          <SelectTrigger data-testid="booking-slot" className={inputCls + " mt-2"}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            {SLOTS.map((s) => (
                              <SelectItem key={s.id} value={s.id} data-testid={`booking-slot-${s.id}`}>
                                {t.schedule.slotNames[s.id]} · {s.time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="label-mono text-ink/70">{t.booking.people}</Label>
                        <Select value={form.people} onValueChange={(v) => set("people", v)}>
                          <SelectTrigger data-testid="booking-people" className={inputCls + " mt-2"}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                              <SelectItem key={n} value={String(n)}>
                                {n}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="label-mono text-ink/70">{t.booking.language}</Label>
                        <Select value={form.language} onValueChange={(v) => set("language", v)}>
                          <SelectTrigger data-testid="booking-language" className={inputCls + " mt-2"}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            {LANGS.map((l) => (
                              <SelectItem key={l.code} value={l.code}>
                                {l.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="md:col-span-2">
                        <Label className="label-mono text-ink/70">{t.booking.notes}</Label>
                        <Textarea
                          data-testid="booking-notes"
                          className="rounded-none border-black/15 bg-transparent mt-2 focus-visible:ring-1 focus-visible:ring-navy font-manrope"
                          rows={3}
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                        />
                      </div>

                      <button
                        type="submit"
                        data-testid="booking-submit"
                        disabled={submitting}
                        className="md:col-span-2 bg-navy text-white label-mono py-4 hover:bg-ocean transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {submitting && <Loader2 size={16} className="animate-spin" />}
                        {submitting ? t.booking.submitting : t.booking.submit}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
